import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, type RouteObject } from "react-router-dom";
import FullScreenLoader from "@/components/feedback/full-screen-loader/FullScreenLoader";
import { ROUTE_COMPONENTS } from "@/routes/registry";
import { HomeRedirect } from "@/routes/RouteFallbacks";
import DesktopPageFrame from "../DesktopPageFrame";

const MobileHome = lazy(() => import("../screens/MobileHome"));
const MobileLeads = lazy(() => import("../screens/MobileLeads"));
const MobileChat = lazy(() => import("../screens/MobileChat"));
const MobileDialer = lazy(() => import("../screens/MobileDialer"));

const withLoader = (node: ReactNode) => <Suspense fallback={<FullScreenLoader />}>{node}</Suspense>;

/** A desktop page inside the shell, by its registry name. */
function desktop(component: string): ReactNode {
  const Comp = ROUTE_COMPONENTS[component];
  return Comp ? (
    <DesktopPageFrame>
      <Comp />
    </DesktopPageFrame>
  ) : (
    <div className="p-6 text-[13px] text-[#697571]">Component missing: {component}</div>
  );
}

const strip = (p: string) => p.replace(/^\/+/, "");

interface TabRoute {
  key?: string;
  path?: string;
  label?: string;
  component: string;
  stageId?: number | null;
}
/** The shape of `/platform/tabs` this builder reads (useRouterData adds icons). */
export interface MobileTabsPayload {
  dashboard?: TabRoute | null;
  whatsapp?: TabRoute | null;
  gmail?: TabRoute | null;
  groups?: { group: string; routes: TabRoute[] }[];
}

/**
 * The phone shell's route table, from the SAME `/platform/tabs` answer the
 * desktop builds its sidebar from (buildDynamicRoutes). Home, the leads
 * list (every stage tab included), Chat and the Dialer are phone screens;
 * every other tab the role carries — settings, reports, modules, the HR
 * suite — stays reachable as its desktop page inside the shell, so nothing a
 * role could open on a computer is missing on a phone.
 */
export function buildMobileRoutes(serverData: MobileTabsPayload | null | undefined): RouteObject[] {
  const routes: RouteObject[] = [];
  const hasDashboard = Boolean(serverData?.dashboard);

  // Home: the dashboard's own numbers when the role has the dashboard tab;
  // without it, Home still greets and points at Leads (the app's GatedHome).
  routes.push({ index: true, element: withLoader(<MobileHome hasDashboard={hasDashboard} />) });
  if (hasDashboard) {
    for (const [path, name] of [
      ["activity", "ActivityPage"],
      ["insights/:dimKey", "KeyInsightsPage"],
    ] as const) {
      if (ROUTE_COMPONENTS[name]) routes.push({ path, element: desktop(name) });
    }
  } else {
    // Keep the desktop's rule for a dashboard-less role's "/": HomeRedirect is
    // not used on the phone (Home exists for everyone), but a stale link to
    // `/activity` must still land somewhere sensible.
    routes.push({ path: "activity", element: <HomeRedirect /> });
  }

  let hasLeadsRoute = false;
  for (const group of serverData?.groups ?? []) {
    for (const route of group?.routes ?? []) {
      if (!route?.path) continue;
      const path = strip(route.path);
      if (route.key === "leads" || route.component === "LeadsPage") {
        hasLeadsRoute = true;
        routes.push({ path, element: withLoader(<MobileLeads />) });
        continue;
      }
      if (route.component === "Dynamics" && route.stageId != null) {
        routes.push({
          path,
          element: withLoader(<MobileLeads stageId={Number(route.stageId)} stageLabel={route.label} />),
        });
        continue;
      }
      routes.push({ path, element: desktop(route.component) });
    }
  }
  if (!hasLeadsRoute) {
    // Drills and notifications navigate to /leads; a role without the tab
    // still gets the list of what it can see (the server scopes it).
    routes.push({ path: "leads", element: withLoader(<MobileLeads />) });
  }

  routes.push({ path: "chat", element: withLoader(<MobileChat />) });
  if (serverData?.whatsapp?.path) {
    routes.push({ path: strip(serverData.whatsapp.path), element: <Navigate to="/chat?seg=whatsapp" replace /> });
  }
  if (serverData?.gmail?.path) {
    const base = strip(serverData.gmail.path);
    const comp = serverData.gmail.component as string;
    routes.push({ path: base, element: desktop(comp) });
    routes.push({ path: `${base}/templates/new`, element: desktop(comp) });
    routes.push({ path: `${base}/templates/:templateId/edit`, element: desktop(comp) });
  }
  routes.push({ path: "dialer", element: withLoader(<MobileDialer />) });

  return routes;
}
